import { Fragment } from 'react';
import { Check } from 'lucide-react';
import { JOB_PUBLISH_STATUS_LABELS, type JobPublishStatus } from '@core/types';
import { JOB_PUBLISH_FLOW_STEPS } from '@core/constants/jobLifecycle';

type Orientation = 'horizontal' | 'vertical';

type Props = { orientation: Orientation; publishStatus: JobPublishStatus | null };

function HeartbeatNode({ accent }: { accent: 'emerald' | 'amber' }) {
  const ring =
    accent === 'amber'
      ? 'border-amber-400 shadow-[0_0_0_3px_rgba(251,191,36,0.22)]'
      : 'border-emerald-400 shadow-[0_0_0_3px_rgba(52,211,153,0.2)]';
  const dot = accent === 'amber' ? 'bg-amber-400' : 'bg-emerald-400';
  const glow = accent === 'amber' ? 'bg-amber-400/30' : 'bg-emerald-400/30';
  return (
    <div className="relative flex h-10 w-10 shrink-0 items-center justify-center" aria-current="step">
      <span className={`absolute inset-[-2px] rounded-full ${glow} animate-heartbeat`} aria-hidden />
      <span
        className={`relative flex h-10 w-10 items-center justify-center rounded-full border-2 ${ring} bg-gray-950`}
      >
        <span className={`h-2.5 w-2.5 rounded-full ${dot}`} />
      </span>
    </div>
  );
}

function CompletedNode() {
  return (
    <div
      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white shadow-sm"
      aria-hidden
    >
      <Check className="h-5 w-5" strokeWidth={2.5} />
    </div>
  );
}

function PendingNode({ n }: { n: number }) {
  return (
    <div
      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 border-[var(--vscode-border-primary)] bg-[color-mix(in_srgb,var(--vscode-bg-tertiary)_85%,transparent)] text-sm font-semibold vscode-text-tertiary"
      aria-hidden
    >
      {n}
    </div>
  );
}

function bracketStepNavLabel(s: string) {
  const t = s.trim();
  if (t.startsWith('[')) return s;
  return `[${t}]`;
}

/** Derives per-step done/active for the four publish stepper milestones. */
function publishStepFlags(ps: JobPublishStatus | null) {
  const s = ps ?? 'draft';
  const done = [false, false, false, false];
  const active = [false, false, false, false];
  const accent: ('emerald' | 'amber')[] = ['emerald', 'emerald', 'emerald', 'emerald'];

  done[0] = s !== 'draft';
  active[0] = s === 'draft';

  done[1] = ['scheduled', 'published', 'paused', 'closed'].includes(s);
  active[1] = s === 'pending_review';

  done[2] = ['published', 'paused', 'closed'].includes(s);
  active[2] = s === 'scheduled';
  if (s === 'scheduled') accent[2] = 'amber';

  done[3] = s === 'closed';
  active[3] = s === 'paused';
  if (s === 'paused') accent[3] = 'amber';

  if (s === 'published') {
    done[0] = true;
    done[1] = true;
    done[2] = true;
    active[0] = false;
    active[1] = false;
    active[2] = false;
  }

  if (s === 'closed') {
    done[0] = true;
    done[1] = true;
    done[2] = true;
    done[3] = true;
    active.fill(false);
  }

  return { done, active, accent, s };
}

export function JobPublishFlowStepper({ orientation, publishStatus }: Props) {
  const steps = JOB_PUBLISH_FLOW_STEPS;
  const last = steps.length - 1;
  const { done, active, accent, s } = publishStepFlags(publishStatus);

  const titleFor = (i: number, base: string) => {
    if (i === 1 && s === 'pending_review') return JOB_PUBLISH_STATUS_LABELS.pending_review;
    if (i === 2 && s === 'scheduled') return JOB_PUBLISH_STATUS_LABELS.scheduled;
    if (i === 2 && s === 'published') return JOB_PUBLISH_STATUS_LABELS.published;
    if (i === 3 && s === 'paused') return JOB_PUBLISH_STATUS_LABELS.paused;
    if (i === 3 && s === 'closed') return JOB_PUBLISH_STATUS_LABELS.closed;
    return base;
  };

  if (orientation === 'horizontal') {
    return (
      <div className="rounded-xl border vscode-border bg-[color-mix(in_srgb,var(--vscode-bg-secondary)_92%,transparent)] p-4 sm:p-5">
        <p className="text-xs font-medium uppercase tracking-wider vscode-text-tertiary mb-4">Publish / careers lifecycle</p>
        <div className="overflow-x-auto pb-1 -mx-1 px-1">
          <div className="flex min-w-[560px] items-center sm:min-w-0">
            {steps.map((step, i) => {
              const isDone = done[i];
              const isActive = active[i];
              const segOk = i < last && done[i] && (done[i + 1] || active[i + 1]);

              return (
                <Fragment key={step.key}>
                  <div className="flex min-w-[100px] flex-1 flex-col items-center text-center px-0.5">
                    {isDone && !isActive ? (
                      <CompletedNode />
                    ) : isActive ? (
                      <HeartbeatNode accent={accent[i]} />
                    ) : (
                      <PendingNode n={i + 1} />
                    )}
                    <p className="mt-2 text-xs font-semibold vscode-text-primary leading-tight">
                      {bracketStepNavLabel(titleFor(i, step.title))}
                    </p>
                    <p className="mt-1 text-[11px] leading-snug vscode-text-tertiary line-clamp-4">{step.description}</p>
                  </div>
                  {i < last ? (
                    <div
                      className={`mx-0.5 h-0.5 w-4 shrink-0 rounded-full sm:w-8 md:flex-1 md:max-w-[40px] ${
                        segOk ? 'bg-emerald-500' : 'bg-gray-700'
                      }`}
                      aria-hidden
                    />
                  ) : null}
                </Fragment>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border vscode-border bg-[color-mix(in_srgb,var(--vscode-bg-secondary)_88%,transparent)] p-4">
      <p className="text-xs font-medium uppercase tracking-wider vscode-text-tertiary mb-4">Where you are (publish)</p>
      <ul className="space-y-0">
        {steps.map((step, i) => {
          const isDone = done[i];
          const isActive = active[i];
          const lineDone = i < last && done[i] && (done[i + 1] || active[i + 1]);

          return (
            <li key={step.key} className="flex gap-4">
              <div className="flex w-10 shrink-0 flex-col items-center">
                {isDone && !isActive ? (
                  <CompletedNode />
                ) : isActive ? (
                  <HeartbeatNode accent={accent[i]} />
                ) : (
                  <PendingNode n={i + 1} />
                )}
                {i < last ? (
                  <div
                    className={`my-1 w-0.5 flex-1 min-h-10 rounded-full ${lineDone ? 'bg-emerald-500' : 'bg-gray-700'}`}
                    aria-hidden
                  />
                ) : null}
              </div>
              <div className={`min-w-0 flex-1 ${i < last ? 'pb-6' : ''}`}>
                <p
                  className={`text-sm font-semibold ${
                    isActive ? 'vscode-text-primary' : isDone && !isActive ? 'text-emerald-100/90' : 'vscode-text-secondary'
                  }`}
                >
                  {bracketStepNavLabel(titleFor(i, step.title))}
                </p>
                <p className="text-xs vscode-text-secondary mt-1 leading-relaxed">{step.description}</p>
                {isActive ? (
                  <p className="text-[11px] text-brand-400/90 mt-2 font-medium">
                    Current stage — use the actions below to move the listing forward.
                  </p>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
